import Cookies from 'js-cookie';
import { getImageUrl } from '@utils/getImageUrl';

/**
 * Load an image URL as a data URL for pdfMake.
 * @param {string} url
 * @param {{ withAuth?: boolean }} [options]
 */
export async function fetchImageAsDataUrl(url, { withAuth = false } = {}) {
    const headers = {};
    if (withAuth) {
        const token = Cookies.get('accessToken');
        if (token) {
            headers.Authorization = `Bearer ${token}`;
        }
    }

    const response = await fetch(url, {
        headers,
        credentials: withAuth ? 'include' : 'same-origin',
    });

    if (!response.ok) {
        throw new Error(`Failed to load image: ${url} (${response.status})`);
    }

    const blob = await response.blob();
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = () => reject(new Error('FileReader failed'));
        reader.readAsDataURL(blob);
    });
}

/**
 * @param {string} dataUrl
 * @returns {Promise<{ width: number, height: number }>}
 */
export function getImageDimensionsFromDataUrl(dataUrl) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
            resolve({
                width: img.naturalWidth || img.width,
                height: img.naturalHeight || img.height,
            });
        };
        img.onerror = () => reject(new Error('Failed to read image dimensions'));
        img.src = dataUrl;
    });
}

/**
 * Scale image to fit inside max box without stretching.
 * @returns {{ width: number, height: number }}
 */
export function scaleImageToFit(naturalWidth, naturalHeight, maxWidth, maxHeight) {
    if (!naturalWidth || !naturalHeight) {
        return { width: maxWidth, height: maxHeight };
    }

    const scale = Math.min(maxWidth / naturalWidth, maxHeight / naturalHeight);
    return {
        width: Math.round(naturalWidth * scale),
        height: Math.round(naturalHeight * scale),
    };
}

/**
 * Resolves API-relative photo path and loads with auth (Bearer token).
 * Returns null if path is missing or load fails (PDF can still render).
 * @param {string} [pathOrUrl]
 * @returns {Promise<{ dataUrl: string, width: number, height: number }|null>}
 */
export async function fetchApiImageAsDataUrl(pathOrUrl) {
    const url = getImageUrl(pathOrUrl);
    if (!url) return null;

    try {
        const dataUrl = await fetchImageAsDataUrl(url, { withAuth: true });
        const { width, height } = await getImageDimensionsFromDataUrl(dataUrl);
        return { dataUrl, width, height };
    } catch (error) {
        console.warn('Could not load API image for PDF:', url, error);
        return null;
    }
}
